import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { User, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfileSettings = ({ user, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    picture: user?.picture || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    try {
      setSaving(true);
      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      };
      
      const response = await axios.patch(`${API}/users/me/profile`, formData, config);
      toast.success('Profile updated successfully!');
      onUpdate(response.data);
      onClose();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Profile Settings
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Email</label>
            <Input 
              value={user?.email || ''} 
              disabled 
              className="bg-slate-100"
            />
            <p className="text-xs text-slate-500 mt-1">
              Email is managed through your Google account
            </p>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Display Name</label>
            <Input 
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter your name"
              data-testid="profile-name-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Picture URL (Optional)</label>
            <Input 
              value={formData.picture}
              onChange={(e) => setFormData({ ...formData, picture: e.target.value })}
              placeholder="https://example.com/photo.jpg"
              data-testid="profile-picture-input"
            />
          </div>

          {formData.picture && (
            <div className="flex justify-center">
              <img 
                src={formData.picture} 
                alt="Preview" 
                className="w-24 h-24 rounded-full object-cover border-2 border-slate-200"
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
          )}

          <div className="flex gap-2 justify-end pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              data-testid="cancel-profile-btn"
            >
              <X className="w-4 h-4 mr-1" />
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving || !formData.name}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="save-profile-btn"
            >
              <Save className="w-4 h-4 mr-1" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileSettings;
